import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { AsyncMessage } from '../components/async-message.js';
import { RouteErrorScreen } from '../screens/errors/route-error-screen.js';
import { ForgotPasswordScreen } from '../screens/forgot-password/forgot-password-screen.js';
import { LoginScreen } from '../screens/login/login-screen.js';
import { ResetPasswordScreen } from '../screens/reset-password/reset-password-screen.js';
import { SetupScreen } from '../screens/setup/setup-screen.js';
import { ProtectedLayout } from './protected-layout.js';

const DashboardScreen = lazy(() => import('../screens/dashboard/dashboard-screen.js'));
const CustomersScreen = lazy(() => import('../screens/customers/customers-screen.js'));
const DebtorsScreen = lazy(() => import('../screens/debtors/debtors-screen.js'));
const FinanceScreen = lazy(() => import('../screens/finance/finance-screen.js'));
const CreditsScreen = lazy(() => import('../screens/financial-assets/credits-screen.js'));
const GiftVouchersScreen = lazy(
  () => import('../screens/financial-assets/gift-vouchers-screen.js'),
);
const PromoCodesScreen = lazy(() => import('../screens/financial-assets/promo-codes-screen.js'));
const QuotesOptionsScreen = lazy(() => import('../screens/commercial/quotes-options-screen.js'));
const ReservationsScreen = lazy(() => import('../screens/reservations/reservations-screen.js'));
const ReservationDetailScreen = lazy(
  () => import('../screens/reservations/reservation-detail-screen.js'),
);
const StaysScreen = lazy(() => import('../screens/stays/stays-screen.js'));
const StayDetailScreen = lazy(() => import('../screens/stays/stay-detail-screen.js'));
const PlanningScreen = lazy(() => import('../screens/planning/planning-screen.js'));
const ResourcesScreen = lazy(() => import('../screens/resources/resources-screen.js'));
const StockScreen = lazy(() => import('../screens/stock/stock-screen.js'));
const EpiScreen = lazy(() => import('../screens/epi/epi-screen.js'));
const LogisticsScreen = lazy(() => import('../screens/logistics/logistics-screen.js'));
const UsersScreen = lazy(() => import('../screens/users/users-screen.js'));
const ProfileScreen = lazy(() => import('../screens/profile/profile-screen.js'));

function lazyScreen(screen: React.ReactNode) {
  return (
    <Suspense fallback={<AsyncMessage kind="loading" title="Chargement de l’écran…" />}>
      {screen}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginScreen />, errorElement: <RouteErrorScreen /> },
  {
    path: '/forgot-password',
    element: <ForgotPasswordScreen />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordScreen />,
    errorElement: <RouteErrorScreen />,
  },
  { path: '/setup', element: <SetupScreen />, errorElement: <RouteErrorScreen /> },
  {
    path: '/',
    element: <ProtectedLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: lazyScreen(<DashboardScreen />) },
      { path: 'customers', element: lazyScreen(<CustomersScreen />) },
      { path: 'stays', element: lazyScreen(<StaysScreen />) },
      { path: 'stays/:stayId', element: lazyScreen(<StayDetailScreen />) },
      { path: 'planning', element: lazyScreen(<PlanningScreen />) },
      { path: 'resources', element: lazyScreen(<ResourcesScreen />) },
      { path: 'stock/:scope', element: lazyScreen(<StockScreen />) },
      { path: 'stock/:scope/items/:itemId', element: lazyScreen(<StockScreen />) },
      { path: 'epi', element: lazyScreen(<EpiScreen />) },
      { path: 'epi/items/:itemId', element: lazyScreen(<EpiScreen />) },
      { path: 'logistics', element: lazyScreen(<LogisticsScreen />) },
      { path: 'logistics/suppliers/:supplierId', element: lazyScreen(<LogisticsScreen />) },
      { path: 'logistics/supplies/:supplyId', element: lazyScreen(<LogisticsScreen />) },
      {
        path: 'logistics/cold-chain/devices/:deviceId',
        element: lazyScreen(<LogisticsScreen />),
      },
      { path: 'reservations', element: lazyScreen(<ReservationsScreen />) },
      { path: 'reservations/:reservationId', element: lazyScreen(<ReservationDetailScreen />) },
      { path: 'quotes', element: lazyScreen(<QuotesOptionsScreen />) },
      { path: 'options', element: lazyScreen(<QuotesOptionsScreen />) },
      { path: 'debtors', element: lazyScreen(<DebtorsScreen />) },
      { path: 'finance', element: lazyScreen(<FinanceScreen />) },
      { path: 'credits', element: lazyScreen(<CreditsScreen />) },
      { path: 'gift-vouchers', element: lazyScreen(<GiftVouchersScreen />) },
      { path: 'promo-codes', element: lazyScreen(<PromoCodesScreen />) },
      { path: 'profile', element: lazyScreen(<ProfileScreen />) },
      { path: 'admin/users', element: lazyScreen(<UsersScreen />) },
    ],
  },
]);
